class BannerVideo {
	constructor(el) {
		this.el = el;
		this.iframe = this.el.querySelector('iframe');
		this.video = this.el.querySelector('video');
		this.videoUrl = this.el.getAttribute('data-video-url');
		this.videoType = this.el.getAttribute('data-video-type');
		this.bannerParentEl = this.el.closest('.banner');
		this.currentSize = '';

		// Check if this is an iframe-based video (YouTube/Vimeo) or HTML5 video
		if (this.iframe && this.videoType === 'embed') {
			// Handle iframe-based video (YouTube/Vimeo)
			this.setupIframe();
		} else if (this.video && (this.videoType === 'local' || this.videoType === 'external')) {
			// Handle HTML5 video element (local or external)
			this.setupHTML5Video();
		} else if (this.el.tagName.toLowerCase() === 'video') {
			// Handle legacy HTML5 video element (responsive videos)
			this.setupHTML5Video();
		} else {
			console.warn('BannerVideo: Invalid element. Please provide a video element or iframe with proper data attributes.');
		}
	}

	setupIframe() {
		const {iframe, videoUrl} = this;
		let src = this.convertToEmbedUrl(videoUrl);

		if (src) {
			if (src.includes('youtube.com')) {
				// Extract video ID for playlist parameter (required for looping)
				const videoIdMatch = src.match(/\/embed\/([a-zA-Z0-9_-]+)/);
				const videoId = videoIdMatch ? videoIdMatch[1] : '';
				
				src += (src.includes('?') ? '&' : '?') + 'autoplay=1&mute=1&loop=1';
				
				// For YouTube loop to work, we need to add the video ID as playlist parameter
				if (videoId) {
					src += `&playlist=${videoId}`;
				}
				
				// Check if there's already a playlist in the URL
				const playlistMatch = src.match(/list=([a-zA-Z0-9_-]+)/);
				if (playlistMatch) {
					src += `&playlist=${playlistMatch[1]}`;
				}
				
				src += '&vq=hd1080'; // Suggest highest quality
			} else if (src.includes('vimeo.com')) {
				src += (src.includes('?') ? '&' : '?') + 'autoplay=1&muted=1&loop=1&quality=1080p';
			} else {
				console.warn('BannerVideo: The video URL is not a recognized YouTube or Vimeo URL.');
				return;
			}

			// Set updated src and allow attributes
			iframe.setAttribute('src', src);
			iframe.setAttribute('allow', 'autoplay; fullscreen'); // Allow autoplay and fullscreen
		} else {
			console.warn('BannerVideo: Could not convert video URL to embed format.');
		}
	}

	setupHTML5Video() {
		// If this is a local or external video, it's already set up in the HTML
		if (this.videoType === 'local' || this.videoType === 'external') {
			// Explicitly enforce autoplay requirements for mobile browsers
			this.video.muted = true;
			this.video.defaultMuted = true;
			this.video.playsInline = true;

			const playVideo = () => {
				this.video.play()
					.then(() => {
						this.setVideoAutoplaySupport(true, false);
					})
					.catch(error => {
						console.warn('BannerVideo: Autoplay failed:', error);
						this.setVideoAutoplaySupport(false, false);
					});
			};

			playVideo();

			// Ensure loop attribute is set for HTML5 videos
			if (!this.video.hasAttribute('loop')) {
				this.video.setAttribute('loop', 'true');
			}
			
			// Add ended event listener as fallback to ensure looping
			this.video.addEventListener('ended', () => {
				this.video.currentTime = 0;
				this.video.play().catch(error => {
					console.warn('BannerVideo: Loop replay failed:', error);
				});
			});
		} else {
			// Legacy responsive video setup
			this.chooseVideoAndPoster();
		}

		// catch playback errors
		const videoElement = this.video || this.el;
		videoElement.addEventListener('error', this.catchPlaybackError);
		videoElement.addEventListener('abort', this.catchPlaybackError);

		// load a new video on resize if needed (only for legacy responsive videos)
		if (!this.videoType || this.videoType === 'responsive') {
			window.addEventListener('resize', () => this.chooseVideoAndPoster());
		}
	}

	convertToEmbedUrl(url) {
		// Convert YouTube watch URLs to embed URLs
		if (url.includes('youtube.com/watch')) {
			const videoId = url.match(/[?&]v=([^&]+)/);
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId[1]}`;
			}
		}
		
		// Convert YouTube short URLs to embed URLs
		if (url.includes('youtu.be/')) {
			const videoId = url.match(/youtu\.be\/([^?]+)/);
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId[1]}`;
			}
		}

		// Convert Vimeo URLs to embed URLs
		if (url.includes('vimeo.com/')) {
			const videoId = url.match(/vimeo\.com\/([^?]+)/);
			if (videoId) {
				return `https://player.vimeo.com/video/${videoId[1]}`;
			}
		}

		// If it's already an embed URL, return as is
		if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com/video')) {
			return url;
		}

		return null;
	}

	setVideoAutoplaySupport(isSupported, removeEl = false) {
		const htmlEl = document.documentElement;

		if (isSupported) {
			htmlEl.classList.add('video-autoplay-support');
			htmlEl.classList.remove('no-video-autoplay-support');
		} else {
			htmlEl.classList.remove('video-autoplay-support');
			htmlEl.classList.add('no-video-autoplay-support');
		}

		if (removeEl) {
			this.el.remove();
		}
	}

	catchPlaybackError = () => {
		// NOTE: Safari will report 'stalled' even when the video is not stalled
		// do not listen for this event
		this.setVideoAutoplaySupport(false, false);
	};

	chooseVideoAndPoster = () => {
		const bannerWidth = window.innerWidth;
		let newSize = '';

		// figure out which device size we're looking at
		if (bannerWidth >= 1024) {
			newSize = 'desktop';
		} else if (bannerWidth >= 641) {
			newSize = 'tablet';
		} else {
			newSize = 'mobile';
		}

		// no need to make changes if the device size hasn't changed
		if (this.currentSize === newSize) {
			return;
		}

		this.currentSize = newSize;

		// select the video and poster URLs to use
		let videoUrl = '';
		let posterUrl = '';

		switch (this.currentSize) {
			case 'desktop':
				videoUrl = this.el.getAttribute('data-video-desktop');
				posterUrl = this.el.getAttribute('data-poster-desktop');
				break;

			case 'tablet':
				videoUrl = this.el.getAttribute('data-video-tablet');
				posterUrl = this.el.getAttribute('data-poster-tablet');
				break;

			case 'mobile':
				videoUrl = this.el.getAttribute('data-video-mobile');
				posterUrl = this.el.getAttribute('data-poster-mobile');
				break;

			default:
				break;
		}

		// ensure we have a video URL
		if (videoUrl) {
			// pause the current video, set/load the new URL, and begin playback
			this.el.pause();

			this.el.setAttribute('src', videoUrl);
			this.el.setAttribute('poster', posterUrl);

			this.el.load();
			this.el.play();
		} else {
			// fallback to banner image for this size if no video URL is set
			this.setVideoAutoplaySupport(false);
		}
	};
}

export default BannerVideo;
